/**
 * SA-441: the widget's Branding tab — grouped Appearance controls that write
 * directly to `widgets.theme_config`, plus SA-440's launcher block (moved
 * here unchanged, since it belongs with the branding controls now) and a
 * WCAG AA contrast warning.
 *
 * Two rules bind every control here:
 *  - An empty input DELETES its key. `theme_config` is a sparse document —
 *    absent means "use the stylesheet default" — so storing "" would be a
 *    different thing entirely, and would fail colour validation on save.
 *  - A contrast pair warns only when BOTH colours parse and the ratio
 *    genuinely fails. An absent colour resolves to the stylesheet default
 *    it will actually render as (`STYLESHEET_DEFAULTS`) before judging; an
 *    authored-but-unparseable colour is not evidence of a problem and still
 *    warns about nothing.
 *
 * The numeric bounds mirror `_BOUNDS` in
 * `backend/app/services/widget/theme_config.py` exactly, so the tab can
 * never offer a value the write lane refuses.
 *
 * A later task adds a live preview to this same tab — see the comment below
 * for where it belongs. This task does not build it.
 */
import { lazy, Suspense, type ChangeEvent } from 'react'
import { Input } from '@/components/ui/Input'
import { NativeSelect } from '@/components/ui/NativeSelect'
import type { ThemeConfig, WidgetData } from '@/hooks/useWidgets'
// Task 1's pure colour maths lives beside the widget it also serves; the
// admin app has no alias into frontend/widget, so this is a relative import
// by design — this file is the sanctioned consumer (task-1-brief.md).
import { parseColor, meetsAA, type Rgb } from '../../../../widget/shared/contrast'
// Whole-branch review, item 3: WidgetPreview pulls in the real Preact widget
// (Preact, @preact/signals, the markdown renderer, its store, its API
// client) and the widget's own CSS string — WidgetManagementPage is a
// static import from App.tsx, so before this, every user of the whole
// product downloaded all of that in the app's eagerly loaded main chunk on
// first paint, whether or not they ever open Admin → Widgets (measured:
// +31.6 kB gzipped). Lazy-loading it moves that weight into its own chunk,
// fetched only when this tab actually mounts a preview.
const WidgetPreview = lazy(() =>
  import('./WidgetPreview').then((mod) => ({ default: mod.WidgetPreview })),
)

type LauncherConfigValue = WidgetData['launcherConfig']

/** Fix round 1: mirrors the light-mode defaults in the `:host,
 *  #nr-widget-root` block of `frontend/widget/styles/widget.css.ts` — the
 *  four colours the widget actually paints when an author leaves the field
 *  blank. `BrandingTab.defaults.test.ts` pins these four values against the
 *  stylesheet itself, so a palette retune can't leave the warning lying
 *  about what will render. Exported so that test can read the same
 *  constants it checks, not a hand-copied second set. */
export const STYLESHEET_DEFAULTS = {
  bg: '#ffffff',
  text: '#1e293b',
  assistant_bubble: '#f1f5f9',
  assistant_text: '#1e293b',
} as const

/** SA-440: one source for the launcher copy. The block moved here from
 *  WidgetManagementPage.tsx unchanged, so these strings (and the tests that
 *  read them) move with it. */
export const LAUNCHER_HINT =
  'Where the chat bubble sits and how big it is. Increase the vertical offset to clear a cookie banner or a fixed footer.'
export const LAUNCHER_HIDDEN_HINT =
  'No bubble is drawn. Your page must open the chat itself with window.nrWidget.open().'

/** An empty input DELETES its key. The backend models a sparse document —
 *  absent means "use the stylesheet default" — so storing "" would be a
 *  different thing entirely, and would fail colour validation on save. */
function setKey<K extends keyof ThemeConfig>(
  value: ThemeConfig, key: K, next: ThemeConfig[K] | '' | undefined,
): ThemeConfig {
  const out = { ...value }
  if (next === '' || next === undefined) delete out[key]
  else out[key] = next as ThemeConfig[K]
  return out
}

/** A pair warns only when BOTH colours parse and the ratio genuinely fails.
 *  Absent and unparseable are different cases (fix round 1): an unset colour
 *  means the stylesheet default WILL render, so it resolves to that default
 *  before judging; an authored-but-unparseable colour means we cannot judge
 *  what will render, so — as before — it warns about nothing.
 *
 *  Whole-branch review, item 7: the spec's §8.3 names three contrast pairs;
 *  this checks two. The third, on-primary vs primary (the header/launcher/
 *  send-button/user-bubble text on the brand colour), is deliberately NOT a
 *  checked pair — it cannot fail. `onColor` (contrast.ts) always picks
 *  whichever of black or white wins against a given primary, so the worst
 *  case is the luminance right at the tie point between the two, which is
 *  still 4.58:1 — above the 4.5:1 AA floor `meetsAA` enforces. There is no
 *  primary_color value this control lets an admin type that can make that
 *  pair fail, so there is nothing for a third `failingPairs` entry to ever
 *  report. Do not add one — it would be a warning that can never fire. */
function failingPairs(theme: ThemeConfig): string[] {
  const pairs: [string, string | undefined, string, string | undefined, string][] = [
    ['Text on background', theme.text, STYLESHEET_DEFAULTS.text, theme.bg, STYLESHEET_DEFAULTS.bg],
    ['Assistant text on its bubble', theme.assistant_text, STYLESHEET_DEFAULTS.assistant_text,
      theme.assistant_bubble, STYLESHEET_DEFAULTS.assistant_bubble],
  ]
  return pairs.flatMap(([label, fg, fgDefault, bg, bgDefault]) => {
    const f = parseColor(fg ?? fgDefault)
    const b = parseColor(bg ?? bgDefault)
    return f && b && !meetsAA(f, b) ? [label] : []
  })
}

/** Fix round 1: the swatch must show what was actually typed, in any format
 *  `parseColor` understands (3/6/8-digit hex, rgb()/rgba(), hsl()/hsla(),
 *  CSS named colours) — not just 6-digit hex. Only a colour `parseColor`
 *  genuinely cannot read falls back to black.
 *
 *  Fix round 2: this list is no longer aspirational — `parseColor` did not
 *  actually read hsl()/hsla() when this comment first claimed it did (a
 *  real AC2 gap: the backend's `_HSL` regex accepts one, so an admin who
 *  types `hsl(340 60% 85%)` as their primary got a pale pink header with
 *  white text left standing on it, unreadable). `contrast.ts` now handles
 *  it for real — legacy comma and modern space/slash syntax, bare or
 *  deg-suffixed hue, wrapped hue, clamped saturation/lightness. */
function toSwatchHex(rgb: Rgb): string {
  const channel = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')
  return `#${channel(rgb.r)}${channel(rgb.g)}${channel(rgb.b)}`
}

type ColorKey = 'primary_color' | 'bg' | 'text' | 'assistant_bubble'

function ColorField({
  id, label, swatchName, colorKey, value, onChange, placeholder,
}: {
  id: string
  label: string
  /** Fix round 1 (minor): a short, distinct name for the swatch's
   *  `aria-label`, deliberately NOT the group label text — four swatches all
   *  named "Colour picker" are indistinguishable to a screen reader, but the
   *  full label ("Primary colour") would collide with `getByLabelText` on
   *  the paired text input, which is the field that carries that name. */
  swatchName: string
  colorKey: ColorKey
  value: ThemeConfig
  onChange: (next: ThemeConfig) => void
  placeholder: string
}) {
  const current = value[colorKey] ?? ''
  const parsed = parseColor(current)
  const swatch = parsed ? toSwatchHex(parsed) : '#000000'
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-foreground mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label={`Colour swatch — ${swatchName}`}
          value={swatch}
          onChange={(e) => onChange(setKey(value, colorKey, e.target.value))}
          className="h-9 w-10 shrink-0 rounded-sm border border-border cursor-pointer"
        />
        <input
          id={id}
          type="text"
          value={current}
          placeholder={placeholder}
          onChange={(e) => onChange(setKey(value, colorKey, e.target.value))}
          className="h-9 flex-1 rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
        />
      </div>
    </div>
  )
}

type NumberKey =
  | 'font_size' | 'border_radius' | 'control_radius' | 'bubble_radius'
  | 'panel_width' | 'panel_height'

/** Shared value/onChange pair for a numeric theme field — empty clears the
 *  key (the sparse-document rule), a non-empty value is stored as a number.
 *  Fix round 1 (nitpick): a value that parses to `NaN` (paste, programmatic
 *  dispatch — the native number control mostly prevents it but does not
 *  guarantee it) is treated the same as empty, never stored. */
function numberField(value: ThemeConfig, key: NumberKey, onChange: (next: ThemeConfig) => void) {
  return {
    value: value[key] ?? '',
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      const parsed = Number(raw)
      onChange(setKey(value, key, raw === '' || Number.isNaN(parsed) ? undefined : parsed))
    },
  }
}

export function BrandingTab(props: {
  value: ThemeConfig
  onChange: (next: ThemeConfig) => void
  /** Present only from WidgetManagementPage.tsx, which owns `launcherConfig`
   *  as a sibling field of `themeConfig`. Omitted (as in this component's
   *  own tests), the launcher section simply does not render. */
  launcher?: LauncherConfigValue
  onLauncherChange?: (next: LauncherConfigValue) => void
}) {
  const { value, onChange, launcher, onLauncherChange } = props
  const warnings = failingPairs(value)

  return (
    <div className="space-y-6">
      {/* Colours */}
      <div>
        <h4 className="text-sm font-medium text-foreground mb-3">Colours</h4>
        <div className="space-y-3">
          <ColorField
            id="theme-primary-color" label="Primary colour" swatchName="primary" colorKey="primary_color"
            value={value} onChange={onChange} placeholder="#2563eb"
          />
          <ColorField
            id="theme-bg" label="Background" swatchName="background" colorKey="bg"
            value={value} onChange={onChange} placeholder="#ffffff"
          />
          <ColorField
            id="theme-text" label="Text" swatchName="text" colorKey="text"
            value={value} onChange={onChange} placeholder="#0f172a"
          />
          <ColorField
            id="theme-assistant-bubble" label="Assistant bubble" swatchName="assistant bubble" colorKey="assistant_bubble"
            value={value} onChange={onChange} placeholder="#f1f5f9"
          />
        </div>
        {warnings.length > 0 && (
          <div role="status" className="mt-3 rounded-lg bg-warning/10 text-warning-foreground px-3 py-2 text-xs">
            Low contrast — {warnings.join('; ')} {warnings.length === 1 ? 'does' : 'do'} not meet WCAG AA.
            The widget will still use these colours; saving is not blocked.
          </div>
        )}
      </div>

      {/* Typography */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-foreground mb-3">Typography</h4>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={!!value.inherit_page_font}
              onChange={(e) => onChange(setKey(value, 'inherit_page_font', e.target.checked ? true : undefined))}
            />
            Inherit the page font
          </label>
          <Input
            label="Body font"
            value={value.font_family ?? ''}
            disabled={!!value.inherit_page_font}
            placeholder="Inter, system-ui, sans-serif"
            onChange={(e) => onChange(setKey(value, 'font_family', e.target.value))}
          />
          <Input
            label="Heading font"
            value={value.heading_font_family ?? ''}
            placeholder="Same as body"
            onChange={(e) => onChange(setKey(value, 'heading_font_family', e.target.value))}
          />
          <Input
            label="Base font size"
            type="number"
            min={10}
            max={32}
            {...numberField(value, 'font_size', onChange)}
          />
        </div>
      </div>

      {/* Shape */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-foreground mb-3">Shape</h4>
        <div className="grid grid-cols-3 gap-3">
          <Input label="Panel radius" type="number" min={0} max={48} {...numberField(value, 'border_radius', onChange)} />
          <Input label="Control radius" type="number" min={0} max={48} {...numberField(value, 'control_radius', onChange)} />
          <Input label="Bubble radius" type="number" min={0} max={48} {...numberField(value, 'bubble_radius', onChange)} />
        </div>
      </div>

      {/* Panel */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-foreground mb-3">Panel</h4>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Panel width" type="number" min={280} max={640} {...numberField(value, 'panel_width', onChange)} />
          <Input label="Panel height" type="number" min={320} max={900} {...numberField(value, 'panel_height', onChange)} />
        </div>
      </div>

      {/* Mode */}
      <div className="border-t pt-4">
        <NativeSelect
          label="Mode"
          value={value.mode ?? 'light'}
          onChange={(e) => onChange({ ...value, mode: e.target.value as ThemeConfig['mode'] })}
        >
          <option value="light">Light</option>
          <option value="dark">Dark</option>
          <option value="auto">Auto</option>
        </NativeSelect>
      </div>

      {/* Preview (SA-441 task 9): the REAL widget component, mounted into an
          iframe with its own viewport — see WidgetPreview.tsx (fix round 1:
          a shadow root can't give `.nr-panel`'s `position: fixed` or the
          mobile media query a viewport to resolve against; fix round 2: the
          two desktop frames size themselves from `panel_width`/`panel_height`
          and the Launcher controls below, so a large panel isn't silently
          clipped, and `launcher` is passed through for real — before this,
          `applyLauncher` ran on frozen defaults and editing an offset below
          visibly changed nothing here). Light and dark side by side so a
          colour choice can be checked in both, plus a narrow instance at the
          widget's own ≤480px breakpoint. */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-foreground mb-3">Preview</h4>
        <Suspense fallback={<div className="text-sm text-muted-foreground">Loading preview…</div>}>
          <div className="flex flex-wrap gap-4">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Light</div>
              <WidgetPreview theme={value} mode="light" launcher={launcher} />
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Dark</div>
              <WidgetPreview theme={value} mode="dark" launcher={launcher} />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-xs text-muted-foreground mb-1">Narrow (≤480px)</div>
            <WidgetPreview
              theme={value} mode={value.mode === 'dark' ? 'dark' : 'light'}
              width={400} launcher={launcher}
            />
          </div>
        </Suspense>
      </div>

      {/* Launcher (SA-440, moved here unchanged) */}
      {launcher && onLauncherChange && (
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-foreground mb-1">Launcher</h4>
          <p className="text-xs text-muted-foreground mb-3">{LAUNCHER_HINT}</p>
          <div className="grid grid-cols-3 gap-3">
            <Input
              label="Launcher size (px)"
              type="number"
              min={32}
              max={96}
              value={launcher.size}
              onChange={(e) => onLauncherChange({ ...launcher, size: Number(e.target.value) })}
            />
            <Input
              label="Horizontal offset (px)"
              type="number"
              min={0}
              max={200}
              value={launcher.offsetX}
              onChange={(e) => onLauncherChange({ ...launcher, offsetX: Number(e.target.value) })}
            />
            <Input
              label="Vertical offset (px)"
              type="number"
              min={0}
              max={200}
              value={launcher.offsetY}
              onChange={(e) => onLauncherChange({ ...launcher, offsetY: Number(e.target.value) })}
            />
          </div>
          <label className="flex items-start gap-2 mt-3 text-sm text-foreground">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={launcher.hidden}
              onChange={(e) => onLauncherChange({ ...launcher, hidden: e.target.checked })}
            />
            <span>
              Hide the launcher
              <span className="block text-xs text-muted-foreground">{LAUNCHER_HIDDEN_HINT}</span>
            </span>
          </label>
        </div>
      )}
    </div>
  )
}
